import * as React from "react";
import apiClient from "@/api-client";
import { useNavigate } from "react-router-dom";

import { useConfirmEmailVerification, useSendEmailVerification } from "@/hooks/api/use-email-verification";

export interface User {
	id: string;
	email: string;
	name: string;
	avatar?: string;
	role?: string;
	isSuperAdmin?: boolean;
	isEmailVerified?: boolean;
	otpEnabled?: boolean;
}

export interface Account {
	id: string;
	name: string;
}

export interface Permissions {
	[key: string]: boolean | string; 
}

interface AuthContextType {
	user: User | null;
	account: Account | null;
	isLoading: boolean;
	isAuthenticated: boolean;
	isEmailVerified: boolean;
	startUrl: string;
	setStartUrl: (url: string) => void;
	getMe: () => Promise<void>;
	setUser: (user: User | null) => void;
	setAccount: (account: Account | null) => void;
	sendEmailVerification: () => Promise<void>;
	confirmEmailVerification: (token: string) => Promise<void>;
	permissions: Permissions;
	setPermissions: (permissions: Permissions) => void;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
	const context = React.useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
	const [user, setUser] = React.useState<User | null>(null);
	const [account, setAccount] = React.useState<Account | null>(null);
	const [permissions, setPermissions] = React.useState<Permissions>({});
	const [isLoading, setIsLoading] = React.useState(true);
  const [startUrl, setStartUrl] = React.useState<string>(() => {
    const pathname = globalThis.location.pathname;
    const search = globalThis.location.search;

    if (pathname === "/") {
      return "/";
    }

    if (pathname === "/sign-in" || pathname === "/forgot-password" || pathname === "/reset-password" || pathname === "/confirm-email") {
      return "/";
    }

    return pathname + search;
  });

	const navigate = useNavigate();

	const isAuthenticated = !!user;
	const isEmailVerified = user?.isEmailVerified ?? false;

	const sendEmailVerificationMutation = useSendEmailVerification();
	const confirmEmailVerificationMutation = useConfirmEmailVerification();

	const getMe = React.useCallback(async (withRedirect = false) => {
		setIsLoading(true);

		try {
			const response = await apiClient.get("/me");
			setUser(response.data.user);
			setAccount(response.data.account);
			setPermissions(response.data.permissions);

			if (withRedirect && response.data.account) {
				navigate("/");
			}

      if (withRedirect && !response.data.account) {
        navigate("/accounts");
      }
		} catch (error) {
			localStorage.removeItem("token");
			localStorage.removeItem("user");
			setUser(null);
			setPermissions({});
      navigate("/sign-in");
			console.error("Failed to load user:", error);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const sendEmailVerification = React.useCallback(async () => {
		await sendEmailVerificationMutation.mutateAsync();
	}, [sendEmailVerificationMutation]);

	const confirmEmailVerification = React.useCallback(
		async (token: string) => {
			await confirmEmailVerificationMutation.mutateAsync({ token });

			setTimeout(async () => {
				await getMe(true);
			}, 2000);
		},
		[confirmEmailVerificationMutation, getMe]
	);

	React.useEffect(() => {
		if (
			globalThis.location.pathname === "/sign-in" ||
			globalThis.location.pathname === "/forgot-password" ||
			globalThis.location.pathname === "/reset-password" ||
			globalThis.location.pathname === "/confirm-email"
		) {
			return;
		}
		getMe();
	}, [getMe]);

	const value: AuthContextType = {
		user,
		account,
		isLoading,
		isAuthenticated,
		isEmailVerified,
		startUrl,
		setStartUrl,
		getMe,
		setUser,
		setAccount,
		sendEmailVerification,
		confirmEmailVerification,
		permissions,
		setPermissions,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
